import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { db, app, functions } from '../config';
import { getFunctions, httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { MarinaSetting } from '../../models/types';
import { v4 as uuidv4 } from 'uuid';
import { getMarinaSettings } from './marinaSettingsService';
import { getSquareConfig } from '../../utils/environment';

// Define response types
interface SquarePaymentResponse {
  success: boolean;
  paymentId?: string;
  error?: string;
  status?: string;
}

interface SquareCustomerResponse {
  success: boolean;
  customerId?: string;
  error?: string;
}

// Utility function to sanitize data for JSON serialization
// This converts BigInt values to strings to avoid serialization issues
const sanitizeForJson = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (typeof obj === 'bigint') {
    return obj.toString();
  }
  
  if (typeof obj === 'object') {
    if (Array.isArray(obj)) {
      return obj.map(item => sanitizeForJson(item));
    } else {
      const result: Record<string, any> = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          result[key] = sanitizeForJson(obj[key]);
        }
      }
      return result;
    }
  }
  
  return obj;
};

// Get Square API credentials from marina settings
const getSquareCredentials = async (): Promise<{ 
  applicationId: string, 
  locationId: string, 
  accessToken: string,
  environment: 'sandbox' | 'production'
}> => {
  try {
    // First try to get from marina settings in Firestore
    const settingsDoc = await getDoc(doc(db, 'settings', 'marinaSettings'));
    
    if (settingsDoc.exists()) {
      const settings = settingsDoc.data() as MarinaSetting;
      
      if (settings.squareApplicationId && settings.squareLocationId && settings.squareAccessToken) {
        return {
          applicationId: settings.squareApplicationId,
          locationId: settings.squareLocationId,
          accessToken: settings.squareAccessToken,
          environment: settings.squareEnvironment || 'production'
        };
      }
    }
    
    // If Firestore settings don't exist or are incomplete, fall back to environment config
    console.log('Using fallback Square config from environment');
    const fallbackConfig = getSquareConfig();
    return {
      ...fallbackConfig,
      environment: settingsDoc.exists() ? 
        (settingsDoc.data() as MarinaSetting).squareEnvironment || fallbackConfig.environment :
        fallbackConfig.environment
    };
  } catch (error) {
    console.error('Error getting Square credentials from settings:', error);
    
    // On error, fall back to environment config
    console.log('Falling back to environment Square config due to error');
    return getSquareConfig();
  }
};

// Get Firebase functions base URL
const getFirebaseFunctionsBaseUrl = () => {
  const region = 'us-central1';
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || app.options.projectId;
  return `https://${region}-${projectId}.cloudfunctions.net`;
};

// Square access token - in a real app, this should be managed server-side
const SQUARE_ACCESS_TOKEN = "EAAAl4Ix3WRq6rEQV2zy4SBWBLz2LP1QIMwyMlBWH-0v2jt97ZTnbojQWP_WV-_1";

// Mock locations data for fallback
const MOCK_LOCATIONS = [
  { id: 'MAIN', name: 'Main Dock' },
  { id: 'MARINA', name: 'Marina Shop' },
  { id: 'RESTAURANT', name: 'Restaurant' }
];

/**
 * Get locations assigned to a staff member
 */
export const getTeamMemberLocations = async (teamMemberId: string) => {
  try {
    console.log('Fetching Square locations for team member:', teamMemberId);
    
    // Get Square credentials from settings
    const credentials = await getSquareCredentials();
    console.log('Credentials obtained for location retrieval');
    
    // Try to use Firebase Function first
    try {
      console.log('Calling Firebase Function getSquareLocations');
      const getLocationsFunction = httpsCallable(functions, 'getSquareLocations');
      const result = await getLocationsFunction({ 
        teamMemberId,
        accessToken: credentials.accessToken,
        environment: credentials.environment
      });
      
      console.log('Successfully retrieved locations from Firebase function');
      // Return the locations from the function result
      // Type assertion for the Firebase function result
      const resultData = result.data as { locations: any[] };
      return resultData.locations || [];
    } catch (functionsError) {
      console.error('Firebase Function error:', functionsError);
      
      // If the function call fails, fall back to direct API call
      console.log('Falling back to direct Square API call');
      
      // Determine API URL based on environment
      const baseUrl = credentials.environment === 'sandbox' 
        ? 'https://connect.squareupsandbox.com' 
        : 'https://connect.squareup.com';
      
      const response = await fetch(`${baseUrl}/v2/locations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${credentials.accessToken}`,
          'Content-Type': 'application/json',
          'Square-Version': '2024-01-17'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Square API error: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Successfully retrieved locations directly from Square API');
      
      if (!data.locations) {
        throw new Error('No locations found');
      }
      
      // If we can't filter by team member, just return all locations
      return data.locations;
    }
  } catch (error) {
    console.error('Error fetching Square locations:', error);
    // Return mock data as a last resort
    console.log('Returning mock location data due to error');
    return MOCK_LOCATIONS;
  }
};

/**
 * Create a new shift for a team member
 */
export const createShift = async (teamMemberId: string, locationId: string) => {
  try {
    // Get Square credentials from settings
    const credentials = await getSquareCredentials();
    
    // Determine API URL based on environment
    const baseUrl = credentials.environment === 'sandbox' 
      ? 'https://connect.squareupsandbox.com' 
      : 'https://connect.squareup.com';
    
    const response = await fetch(`${baseUrl}/v2/labor/shifts`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${credentials.accessToken}`,
        'Content-Type': 'application/json',
        'Square-Version': '2024-01-17'
      },
      body: JSON.stringify({
        idempotency_key: `${Date.now()}`,
        shift: {
          team_member_id: teamMemberId,
          location_id: locationId,
          start_at: new Date().toISOString(),
          status: 'OPEN'
        }
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to create shift');
    }

    const data = await response.json();
    return data.shift;
  } catch (error) {
    console.error('Error creating shift:', error);
    // For demo purposes, return a mock shift
    return {
      id: `mock-shift-${Date.now()}`,
      team_member_id: teamMemberId,
      location_id: locationId,
      start_at: new Date().toISOString(),
      status: 'OPEN'
    };
  }
};

/**
 * Update an existing shift (clock out)
 */
export const updateShift = async (shiftId: string, shiftData: any) => {
  try {
    // Get Square credentials from settings
    const credentials = await getSquareCredentials();
    
    // Determine API URL based on environment
    const baseUrl = credentials.environment === 'sandbox' 
      ? 'https://connect.squareupsandbox.com' 
      : 'https://connect.squareup.com';
    
    const response = await fetch(`${baseUrl}/v2/labor/shifts/${shiftId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${credentials.accessToken}`,
        'Content-Type': 'application/json',
        'Square-Version': '2024-01-17'
      },
      body: JSON.stringify({
        shift: {
          ...shiftData,
          end_at: new Date().toISOString(),
          status: 'CLOSED'
        }
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to update shift');
    }

    const data = await response.json();
    return data.shift;
  } catch (error) {
    console.error('Error updating shift:', error);
    // Just return success for demo purposes
    return null;
  }
};

/**
 * Creates a payment using Square payment processor
 * @param sourceId The nonce or token from Square payment form
 * @param amount The payment amount in dollars
 * @param currency The currency code (default: USD)
 * @param description Optional payment description
 * @returns The payment result from Square
 */
export const createPayment = async (
  sourceId: string,
  amount: number,
  currency: string = 'USD',
  description: string = 'Marina reservation',
  customerInfo?: any
): Promise<SquarePaymentResponse> => {
  try {
    // Use the direct HTTP endpoint to avoid BigInt serialization issues
    console.log('Using direct HTTP endpoint for Square payment processing');
    const baseUrl = getFirebaseFunctionsBaseUrl();
    const url = `${baseUrl}/processSquarePaymentDirect`;
    
    console.log('Getting Square credentials for payment processing');
    const credentials = await getSquareCredentials();
    console.log('Payment credentials obtained', { 
      hasLocationId: !!credentials.locationId 
    });
    
    // Generate a unique idempotency key to prevent duplicate payments
    const idempotencyKey = `payment_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    console.log('Generated idempotency key for payment:', idempotencyKey);
    
    const requestData = {
      sourceId,
      amount,
      currency,
      description,
      locationId: credentials.locationId,
      idempotencyKey,
      customerInfo,
      // Pass Square credentials directly to avoid Firestore permission issues
      accessToken: credentials.accessToken,
      environment: credentials.environment
    };
    
    console.log('Making payment HTTP request to', url);
    console.log('Payment request data:', {
      hasSourceId: !!sourceId,
      amount,
      currency,
      hasLocationId: !!credentials.locationId,
      hasIdempotencyKey: !!idempotencyKey,
      hasCustomerInfo: !!customerInfo
    });
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      console.log('Payment HTTP response status:', response.status);
      
      // Try to get the response body even if status is not OK
      const responseText = await response.text();
      console.log('Payment response body:', responseText);
      
      let result;
      try {
        // Try to parse the response as JSON
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parsing payment response as JSON:', parseError);
        throw new Error(`Server returned invalid JSON: ${responseText}`);
      }
      
      if (!response.ok) {
        console.error('HTTP error response for payment:', result);
        
        // Extract the most specific error message from the details array
        let errorMessage = result.error || `HTTP error! status: ${response.status}`;
        
        // Check if we have detailed error information
        if (result.details && Array.isArray(result.details) && result.details.length > 0) {
          // Find CVV error first as it's more specific
          const cvvError = result.details.find(err => err.code === 'CVV_FAILURE');
          const avsError = result.details.find(err => err.code === 'ADDRESS_VERIFICATION_FAILURE');
          const cardError = result.details.find(err => err.category === 'PAYMENT_METHOD_ERROR');
          
          // Use the most specific error message available
          if (cvvError) {
            errorMessage = 'Card security code (CVV) is incorrect. Please check and try again.';
          } else if (avsError) {
            errorMessage = 'The billing address does not match the card. Please check and try again.';
          } else if (cardError) {
            errorMessage = `Card declined: ${cardError.detail || cardError.code}`;
          }
          
          console.log('Detailed payment error information:', result.details);
        }
        
        throw new Error(errorMessage);
      }
      
      return result as SquarePaymentResponse;
    } catch (fetchError) {
      console.error('Fetch error in payment:', fetchError);
      throw fetchError;
    }
  } catch (error) {
    console.error('Error creating Square payment:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Creates a customer in Square
 * @param firstName The customer's first name
 * @param lastName The customer's last name
 * @param email The customer's email address
 * @param phone Optional phone number
 * @returns The customer result from Square
 */
export const createCustomer = async (
  firstName: string,
  lastName: string,
  email: string,
  phone?: string
): Promise<SquareCustomerResponse> => {
  try {
    // Use the direct HTTP endpoint to avoid BigInt serialization issues
    console.log('Using direct HTTP endpoint for Square customer creation');
    const baseUrl = getFirebaseFunctionsBaseUrl();
    const url = `${baseUrl}/createSquareCustomerDirect`;
    
    console.log('Getting Square credentials for customer creation');
    const credentials = await getSquareCredentials();
    console.log('Credentials obtained', {
      hasApplicationId: !!credentials.applicationId,
      hasLocationId: !!credentials.locationId,
      hasAccessToken: !!credentials.accessToken, 
      environment: credentials.environment
    });
    
    const requestData = {
      firstName,
      lastName,
      email,
      phone,
      // Pass Square credentials directly to avoid Firestore permission issues
      accessToken: credentials.accessToken,
      environment: credentials.environment
    };
    
    console.log('Making HTTP request to', url);
    console.log('Request data:', {
      firstName,
      lastName,
      email,
      phone
    });
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      console.log('HTTP response status:', response.status);
      
      // Try to get the response body even if status is not OK
      const responseText = await response.text();
      console.log('Response body:', responseText);
      
      let result;
      try {
        // Try to parse the response as JSON
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parsing response as JSON:', parseError);
        throw new Error(`Server returned invalid JSON: ${responseText}`);
      }
      
      if (!response.ok) {
        console.error('HTTP error response:', result);
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }
      
      return result as SquareCustomerResponse;
    } catch (fetchError) {
      console.error('Fetch error:', fetchError);
      throw fetchError;
    }
  } catch (error) {
    console.error('Error creating Square customer:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Searches for an existing customer in Square by email address
 * @param email The customer's email address
 * @returns Customer ID if found, null if not found
 */
export const findCustomerByEmail = async (
  email: string
): Promise<string | null> => {
  try {
    console.log('Searching for existing customer with email:', email);
    
    // Get Square configuration
    const credentials = await getSquareCredentials();
    console.log('Credentials obtained for customer search');
    
    // Use the direct HTTP endpoint
    const baseUrl = getFirebaseFunctionsBaseUrl();
    const url = `${baseUrl}/findSquareCustomerDirect`;
    
    const requestData = {
      email,
      accessToken: credentials.accessToken,
      environment: credentials.environment
    };
    
    console.log('Making Square customer search request');
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      console.log('Customer search HTTP response status:', response.status);
      
      // Parse the response
      const responseText = await response.text();
      console.log('Customer search response body:', responseText);
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parsing customer search response as JSON:', parseError);
        return null;
      }
      
      if (!response.ok || result.error) {
        console.error('HTTP error response for customer search:', result);
        // Continue with customer creation instead of failing
        return null;
      }
      
      // Return customer ID if found, otherwise null
      return result.customerId || null;
    } catch (fetchError) {
      console.error('Fetch error in customer search:', fetchError);
      // If there's a CORS error or network issue, continue without customer lookup
      console.log('Continuing without customer lookup due to network/CORS issue');
      return null;
    }
  } catch (error) {
    console.error('Error searching for Square customer:', error);
    // Return null to allow customer creation to proceed
    return null;
  }
};

// Add a function to process refunds through Square API
export const processRefund = async (
  paymentId: string,
  amount: number,
  reason: string
): Promise<{ success: boolean; refundId?: string; status?: string; error?: string }> => {
  try {
    // First get the Square configuration from marina settings
    const settings = await getMarinaSettings();
    
    if (!settings.squareAccessToken) {
      throw new Error('Square access token not configured');
    }
    
    // Use the direct HTTP endpoint via our Cloud Function to avoid CORS issues
    console.log('Using Cloud Function proxy for Square refund');
    const baseUrl = getFirebaseFunctionsBaseUrl();
    const url = `${baseUrl}/processSquareRefundDirect`;
    
    const requestData = {
      paymentId,
      amount,
      reason,
      accessToken: settings.squareAccessToken,
      environment: settings.squareEnvironment || 'sandbox',
      idempotencyKey: uuidv4() // Ensure unique refund attempts
    };
    
    console.log('Making refund request via Cloud Function proxy');
    console.log('Refund details:', { 
      paymentId, 
      amount, 
      reason_length: reason.length,
      environment: settings.squareEnvironment || 'sandbox'
    });
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      // Try to get the response body even if status is not OK
      const responseText = await response.text();
      console.log('Refund response status:', response.status);
      console.log('Refund response body:', responseText);
      
      let result;
      try {
        // Try to parse the response as JSON
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parsing refund response as JSON:', parseError);
        throw new Error(`Server returned invalid JSON: ${responseText}`);
      }
      
      if (!response.ok) {
        console.error('HTTP error response for refund:', result);
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }
      
      // No need to log the refund here - the Cloud Function already logs it to Firestore
      // This avoids permission issues since the Cloud Function has the necessary permissions
      
      return {
        success: true,
        refundId: result.refundId,
        status: result.status || 'PENDING'
      };
    } catch (fetchError) {
      console.error('Fetch error in refund:', fetchError);
      throw fetchError;
    }
  } catch (error) {
    console.error('Error processing Square refund:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error processing refund'
    };
  }
}; 